import { useState, forwardRef, InputHTMLAttributes, useRef, useEffect, useCallback } from 'react';
import type { ChangeEvent, MutableRefObject } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

interface NumberInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  value?: string | number;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  showPad?: boolean;
  showSpinner?: boolean;
}

/**
 * NumberInput - A number input component with an optional cellphone-style number pad popover
 *
 * @param value - The input value (controlled)
 * @param onChange - Change handler that receives a synthetic event
 * @param showPad - Whether to show the number pad popover (default: true)
 * @param showSpinner - Whether to show browser spinner buttons (default: false)
 * @param ...props - All standard HTML input attributes (min, max, step, placeholder, etc.)
 */
const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      value = '',
      onChange,
      showPad = true,
      showSpinner = false,
      className = '',
      placeholder = 'Enter number',
      disabled = false,
      min,
      max,
      step = 1,
      ...props
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);
    const [internalValue, setInternalValue] = useState(value?.toString() || '');
    const internalInputRef = useRef<HTMLInputElement | null>(null);

    // Sync internal value with external value prop
    if (value !== undefined && value?.toString() !== internalValue) {
      setInternalValue(value?.toString() || '');
    }

    // Keep input focused when popover opens
    useEffect(() => {
      if (open && internalInputRef.current) {
        // Small delay to ensure popover has rendered
        const timer = setTimeout(() => {
          internalInputRef.current?.focus();
        }, 0);
        return () => clearTimeout(timer);
      }
    }, [open]);

    const createSyntheticEvent = (newValue: string): ChangeEvent<HTMLInputElement> =>
      ({
        target: { value: newValue, name: props.name } as HTMLInputElement,
        currentTarget: { value: newValue } as HTMLInputElement,
      }) as ChangeEvent<HTMLInputElement>;

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInternalValue(newValue);
      onChange?.(e);
    };

    const handlePadInput = (newValue: string) => {
      // Validate against min/max if provided
      const numValue = parseFloat(newValue);
      const maxNum = typeof max === 'string' ? parseFloat(max) : max;
      const minNum = typeof min === 'string' ? parseFloat(min) : min;
      if (maxNum !== undefined && !Number.isNaN(maxNum) && numValue > maxNum) return;
      if (minNum !== undefined && !Number.isNaN(minNum) && numValue < minNum) return;

      setInternalValue(newValue);
      onChange?.(createSyntheticEvent(newValue));
    };

    const handlePadClick = useCallback(
      (digit: string) => {
        handlePadInput(internalValue + digit);
        // Refocus the input after clicking
        internalInputRef.current?.focus();
      },
      [internalValue],
    );

    const handleBackspace = useCallback(() => {
      const newValue = internalValue.slice(0, -1);
      setInternalValue(newValue);
      onChange?.(createSyntheticEvent(newValue));
      // Refocus the input after clicking
      internalInputRef.current?.focus();
    }, [internalValue, onChange]);

    const handleClear = useCallback(() => {
      setInternalValue('');
      onChange?.(createSyntheticEvent(''));
      // Refocus the input after clicking
      internalInputRef.current?.focus();
    }, [onChange]);

    const setInputRef = useCallback(
      (node: HTMLInputElement | null) => {
        internalInputRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as MutableRefObject<HTMLInputElement | null>).current = node;
        }
      },
      [ref],
    );

    const NumberPad = () => (
      <div className="tw-grid tw-grid-cols-3 tw-gap-2 tw-p-3 tw-w-56">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
          <button
            key={num}
            onPointerDown={e => {
              e.preventDefault(); // Prevent focus loss
              handlePadClick(num.toString());
            }}
            className="tw-h-12 tw-rounded-lg tw-bg-white tw-border tw-border-neutral-100 tw-text-neutral-950 hover:tw-bg-neutral-100 active:tw-bg-neutral-200 tw-transition-colors tw-flex tw-items-center tw-justify-center"
            style={{
              fontFamily: 'var(--font-atkinson)',
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-weight-bold)',
              borderRadius: 'var(--radius)',
              backgroundColor: 'var(--card)',
              color: 'var(--foreground)',
              borderColor: 'var(--border)',
            }}
            type="button"
          >
            {num}
          </button>
        ))}
        <button
          onPointerDown={e => {
            e.preventDefault(); // Prevent focus loss
            handleClear();
          }}
          className="tw-h-12 tw-rounded-lg tw-bg-white tw-border tw-border-neutral-100 tw-text-neutral-950 hover:tw-bg-neutral-100 active:tw-bg-neutral-200 tw-transition-colors tw-flex tw-items-center tw-justify-center"
          style={{
            fontFamily: 'var(--font-atkinson)',
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-weight-bold)',
            borderRadius: 'var(--radius)',
            backgroundColor: 'var(--card)',
            color: 'var(--foreground)',
            borderColor: 'var(--border)',
          }}
          type="button"
        >
          Clear
        </button>
        <button
          onPointerDown={e => {
            e.preventDefault(); // Prevent focus loss
            handlePadClick('0');
          }}
          className="tw-h-12 tw-rounded-lg tw-bg-white tw-border tw-border-neutral-100 tw-text-neutral-950 hover:tw-bg-neutral-100 active:tw-bg-neutral-200 tw-transition-colors tw-flex tw-items-center tw-justify-center"
          style={{
            fontFamily: 'var(--font-atkinson)',
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-weight-bold)',
            borderRadius: 'var(--radius)',
            backgroundColor: 'var(--card)',
            color: 'var(--foreground)',
            borderColor: 'var(--border)',
          }}
          type="button"
        >
          0
        </button>
        <button
          onPointerDown={e => {
            e.preventDefault(); // Prevent focus loss
            handleBackspace();
          }}
          className="tw-h-12 tw-rounded-lg tw-bg-white tw-border tw-border-neutral-100 tw-text-neutral-950 hover:tw-bg-neutral-100 active:tw-bg-neutral-200 tw-transition-colors tw-flex tw-items-center tw-justify-center"
          style={{
            fontFamily: 'var(--font-atkinson)',
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-weight-bold)',
            borderRadius: 'var(--radius)',
            backgroundColor: 'var(--card)',
            color: 'var(--foreground)',
            borderColor: 'var(--border)',
          }}
          type="button"
        >
          ⌫
        </button>
      </div>
    );

    if (!showPad) {
      return (
        <>
          {!showSpinner && (
            <style>{`
            input[type=number].hide-spinner::-webkit-inner-spin-button,
            input[type=number].hide-spinner::-webkit-outer-spin-button {
              -webkit-appearance: none;
              margin: 0;
            }
            input[type=number].hide-spinner {
              -moz-appearance: textfield;
            }
          `}</style>
          )}
          <input
            ref={ref}
            type="number"
            value={internalValue}
            onChange={handleInputChange}
            placeholder={placeholder}
            disabled={disabled}
            min={min}
            max={max}
            step={step}
            className={`hide-spinner tw-flex tw-h-10 tw-w-full tw-rounded-md tw-border tw-border-border tw-bg-input-background tw-px-3 tw-py-2 placeholder:tw-text-muted-foreground focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-ring focus:tw-ring-offset-2 disabled:tw-cursor-not-allowed disabled:tw-opacity-50 ${className}`}
            style={{
              fontFamily: 'var(--font-atkinson)',
              fontSize: 'var(--text-base)',
              fontWeight: 'var(--font-weight-normal)',
              borderRadius: 'var(--radius)',
              borderColor: 'var(--border)',
            }}
            {...props}
          />
        </>
      );
    }

    return (
      <>
        {!showSpinner && (
          <style>{`
          input[type=number].hide-spinner::-webkit-inner-spin-button,
          input[type=number].hide-spinner::-webkit-outer-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          input[type=number].hide-spinner {
            -moz-appearance: textfield;
          }
        `}</style>
        )}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <input
              ref={setInputRef}
              type="number"
              value={internalValue}
              onChange={handleInputChange}
              placeholder={placeholder}
              disabled={disabled}
              min={min}
              max={max}
              step={step}
              className={`hide-spinner tw-flex tw-h-10 tw-w-full tw-rounded-md tw-border tw-border-border tw-bg-input-background tw-px-3 tw-py-2 placeholder:tw-text-muted-foreground focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-ring focus:tw-ring-offset-2 disabled:tw-cursor-not-allowed disabled:tw-opacity-50 ${className}`}
              style={{
                fontFamily: 'var(--font-atkinson)',
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-weight-normal)',
                borderRadius: 'var(--radius)',
                borderColor: 'var(--border)',
              }}
              {...props}
            />
          </PopoverTrigger>
          <PopoverContent className="tw-w-auto tw-p-0" align="start">
            <NumberPad />
          </PopoverContent>
        </Popover>
      </>
    );
  },
);

NumberInput.displayName = 'NumberInput';

export default NumberInput;
